import {
  Options,
  RangeSelectorButtonTypeValue,
  SeriesLegendItemClickEventObject,
  SeriesSankeyDataLabelsFormatterContextObject,
  SVGElement
} from 'highcharts'
import { DateTime } from 'luxon'
import { computed, reactive, ref, watch } from 'vue'

import { getYAxisOptions } from '../../src/composables/helper/axis'
import { getHighchartsColorSet } from '../../src/composables/helper/color-set'
import { commonParser, pieParser, sankeyParser } from '../../src/composables/helper/series-parser'
import { chartFontStyle, legendOptions } from '../../src/composables/helper/static-options'
import { CHART_TYPE, VIEW_PERIOD, XAXIS_TYPE } from '../../src/constants/chart-related.constant'
import { DATE_FORMAT } from '../../src/constants/date.constant'
import { IChartWatermark } from '../../src/types'
import { IChartContainer } from '../../src/types/chart-conatiner.type'
import { IChartOptions, ICustomChart } from '../../src/types/chart-options.type'
import { IPointExtendNode, ISankeyTooltipFormatterObject } from '../../src/types/chart-tooltip.type'
import { IHighchartsSeries } from '../../src/types/default-series.type'
import { addWatermark, getDefaultChartContainer, getDefaultChartOptions } from '../../src/utils/chart.util'
import {
  defaultTooltipFormatter,
  legendFormatter,
  pieChartTooltipFormatter,
  sankeyChartTooltipFormatter,
  sankeyNodeFormatter,
  xAxisFormatter
} from '../../src/utils/formatter.util'
/*
* 사용법
*
* chartContainer
  title: string                 // 차트 타이틀 (default '')
  additionalFunc: {
    useDownload: boolean        // series download(미구현) (default false)
    useScreenshot: boolean      // 스크린샷(미구현) (default false)
    useShare: boolean           // 공유(미구현) (default false)
  }
  chartFunc: {
    useDataGroup: boolean       // 데이터 그룹핑(미구현, 서버측과 논의 필요) (default false)
    useLinearLog: boolean       // 선형/로그 스케일(미구현) (default false)
    useMovingAverage: boolean   // 이동평균(미구현) (default false)
  }
  description: string           // 디스크립션 (default '')
  source: string                // 출처 (default '')
*
*
* chartOptions
  viewPeriodOptions: {
    showPeriodPicker: boolean             // 차트에서 보여줄 기간 버튼 노출 여부 (default false)
    viewPeriodList: VIEW_PERIOD_PICKER    // 차트에서 보여줄 선택가능한 기간리스트
    selectedViewPeriod: VIEW_PERIOD       // 차트에서 보여줄 기간 (default all)
    stDate: Date                          // DatePicker에서 선택한 시작일
    endDate: Date                         // DatePicker에서 선택한 종료일
  }
  xAxisOptions: {
    title: string                         // x축 타이틀 (default '')
    type: XAXIS_TYPE | string             // x축 타입 (datetime, category) (default datetime)
    categories: string[]                  // x축 카테고리리스트(가로차트에서 사용)
  }
  yAxisOptions: {
    useAbsMinMaxBySeries: boolean         // y축 최소나 최댓값의 절대값 기준으로 가운데를 0으로 하여 중간에 그리게 하는 옵션 (default false)
    left: {
      title: string                       // 좌측 y축 타이틀 (default '')
      type: YAXIS_TYPE | string           // 좌측 y축 타입 (선형, 로그)
      unit: string                        // 좌측 y축 단위 (default '')
      softMin: number                     // 좌측 y축 소프트최소값 (default 0)
      max: number | null                  // 좌측 y축 최대값 (default null)
    }
    right: {
      title: string                       // 우측 y축 타이틀 (default '')
      type: YAXIS_TYPE | string           // 우측 y축 타입 (선형, 로그)
      unit: string                        // 우측 y축 단위 (default '')
      softMin: number                     // 우측 y축 소프트최소값 (default 0)
      max: number | null                  // 우측 y축 최대값 (default null)
    }
  }
  tooltipOptions: {
    dataGroup: DATA_GROUP                 // 데이터 그룹핑 시 날짜를 범위로 보여줌 (default day)
    title: string                         // 툴팁 타이틀 (default '')
    dateFormat: DATE_FORMAT               // 툴팁 날짜포맷 (default yyyy-MM-dd)
    commonContents: string                // pie 차트 공통 툴팁 내용 (default '')
  }
  showNavigator: boolean                  // 차트 하단 네비게이션 노출 여부 (default false)
  chartDataList: {
    name: string                              // 데이터 이름
    yAxis: YAXIS_POSITION                     // y축 기준
    type: CHART_TYPE                          // 차트 타입
    stackingType: CHART_STACKING_TYPE         // 누적타입(bar, column, area)(미누적, 누적, 비중)
    unit: string                              // 단위
    decimalPoint: number                      // 소수점 자리수
    data: Record<string, string | number>[]   // 데이터
  }[]
*
*
* highChartOptions >> highcharts 컴포넌트에 options에 적용
* */

/*
* 사용법
*
* chartContainer
  title: string                 // 차트 타이틀 (default '')
  additionalFunc: {
    useDownload: boolean        // series download(미구현) (default false)
    useScreenshot: boolean      // 스크린샷(미구현) (default false)
    useShare: boolean           // 공유(미구현) (default false)
  }
  chartFunc: {
    useDataGroup: boolean       // 데이터 그룹핑(미구현, 서버측과 논의 필요) (default false)
    useLinearLog: boolean       // 선형/로그 스케일(미구현) (default false)
    useMovingAverage: boolean   // 이동평균(미구현) (default false)
  }
  description: string           // 디스크립션 (default '')
  source: string                // 출처 (default '')
*
*
* chartOptions
  viewPeriodOptions: {
    showPeriodPicker: boolean             // 차트에서 보여줄 기간 버튼 노출 여부 (default false)
    viewPeriodList: VIEW_PERIOD_PICKER    // 차트에서 보여줄 선택가능한 기간리스트
    selectedViewPeriod: VIEW_PERIOD       // 차트에서 보여줄 기간 (default all)
    stDate: Date                          // DatePicker에서 선택한 시작일
    endDate: Date                         // DatePicker에서 선택한 종료일
  }
  xAxisOptions: {
    title: string                         // x축 타이틀 (default '')
    type: XAXIS_TYPE | string             // x축 타입 (datetime, category) (default datetime)
    categories: string[]                  // x축 카테고리리스트(가로차트에서 사용)
  }
  yAxisOptions: {
    useAbsMinMaxBySeries: boolean         // y축 최소나 최댓값의 절대값 기준으로 가운데를 0으로 하여 중간에 그리게 하는 옵션 (default false)
    left: {
      title: string                       // 좌측 y축 타이틀 (default '')
      type: YAXIS_TYPE | string           // 좌측 y축 타입 (선형, 로그)
      unit: string                        // 좌측 y축 단위 (default '')
      softMin: number                     // 좌측 y축 소프트최소값 (default 0)
      max: number | null                  // 좌측 y축 최대값 (default null)
    }
    right: {
      title: string                       // 우측 y축 타이틀 (default '')
      type: YAXIS_TYPE | string           // 우측 y축 타입 (선형, 로그)
      unit: string                        // 우측 y축 단위 (default '')
      softMin: number                     // 우측 y축 소프트최소값 (default 0)
      max: number | null                  // 우측 y축 최대값 (default null)
    }
  }
  tooltipOptions: {
    dataGroup: DATA_GROUP                 // 데이터 그룹핑 시 날짜를 범위로 보여줌 (default day)
    title: string                         // 툴팁 타이틀 (default '')
    dateFormat: DATE_FORMAT               // 툴팁 날짜포맷 (default yyyy-MM-dd)
    commonContents: string                // pie 차트 공통 툴팁 내용 (default '')
  }
  showNavigator: boolean                  // 차트 하단 네비게이션 노출 여부 (default false)
  chartDataList: {
    name: string                              // 데이터 이름
    yAxis: YAXIS_POSITION                     // y축 기준
    type: CHART_TYPE                          // 차트 타입
    stackingType: CHART_STACKING_TYPE         // 누적타입(bar, column, area)(미누적, 누적, 비중)
    unit: string                              // 단위
    decimalPoint: number                      // 소수점 자리수
    data: Record<string, string | number>[]   // 데이터
  }[]
*
*
* highChartOptions >> highcharts 컴포넌트에 options에 적용
* */



export const useXangleChart = (watermark?: IChartWatermark, isDark?: boolean) => {
  const chartContainer = reactive<IChartContainer>(getDefaultChartContainer())
  const chartOptions = reactive<IChartOptions>(getDefaultChartOptions())
  let watermarkImg: SVGElement | null

  const disabledSeries = ref<string[]>([])
  const xAxisIntervalTime = ref(24 * 3600 * 1000)

  const selectedPeriodIdx = computed(() => {
    return chartOptions.viewPeriodOptions.viewPeriodList.findIndex(
      item => item.value === chartOptions.viewPeriodOptions.selectedViewPeriod,
    ) === -1
      ? chartOptions.viewPeriodOptions.viewPeriodList.length - 1
      : chartOptions.viewPeriodOptions.viewPeriodList.findIndex(
        item => item.value === chartOptions.viewPeriodOptions.selectedViewPeriod,
      )
  })
  const seriesColors = computed(() => {
    return getHighchartsColorSet(chartOptions.chartDataList.length, isDark)
  })
  const chartType = computed(() => {
    if (chartOptions.chartDataList.find(ele => ele.type === CHART_TYPE.PIE)) {
      return CHART_TYPE.PIE
    } else if (chartOptions.chartDataList.find(ele => ele.type === CHART_TYPE.DONUT)) {
      return CHART_TYPE.DONUT
    } else if (chartOptions.chartDataList.find(ele => ele.type === CHART_TYPE.TABLE)) {
      return CHART_TYPE.TABLE
    } else if (chartOptions.chartDataList.find(ele => ele.type === CHART_TYPE.SANKEY)) {
      return CHART_TYPE.SANKEY
    } else {
      return CHART_TYPE.COMMON
    }
  })
  const parsedChartSeries = computed(() => {
    switch (chartType.value) {
      case CHART_TYPE.PIE:
      case CHART_TYPE.DONUT:
        return pieParser(chartOptions.chartDataList)
      case CHART_TYPE.TABLE:
        return []
      case CHART_TYPE.SANKEY:
        return sankeyParser(chartOptions.chartDataList)
      default:
        return commonParser(chartOptions.chartDataList)
    }
  })
  const xAxisCategories = computed(()=> {
    if (chartOptions.xAxisOptions.categories.length > 0) {
      return chartOptions.xAxisOptions.categories
    }
    if (chartOptions.xAxisOptions.standard) {
      const result: string[] = []
      chartOptions.chartDataList.map(ele => {
        return ele.data.map(innerEle => {
          result.push(String(innerEle[chartOptions.xAxisOptions.standard]))
        })
      })
      return result
    }
    return []
  })

  const navigatorOffset = computed(() => {
    if (
      chartOptions.viewPeriodOptions.selectedViewPeriod === VIEW_PERIOD.SELECTOR &&
        chartOptions.viewPeriodOptions.stDate &&
        chartOptions.viewPeriodOptions.endDate &&
        !(chartType.value === CHART_TYPE.PIE || chartType.value === CHART_TYPE.DONUT) &&
        chartOptions.chartDataList.length > 0
    ) {
      let seriesMinMills = Infinity
      let seriesMaxMills = -Infinity
      for (const ele of parsedChartSeries.value) {
        const eachData = ele as IHighchartsSeries
        const min = Math.min(...eachData.data.map(ele => {
          // if (Array.isArray(ele)) {
          //   return ele[0]
          // }
          return ele.x
        }))
        const max = Math.max(...eachData.data.map(ele => {
          // if (Array.isArray(ele)) {
          //   return ele[0]
          // }
          return ele.x
        }))
        seriesMinMills = Math.min(seriesMinMills, min)
        seriesMaxMills = Math.max(seriesMaxMills, max)
      }
      // const selectMinDate = DateTime.fromFormat(chartOptions.viewPeriodOptions.stDate, DATE_FORMAT.DEFAULT_FORMAT).minus({
      //   day: 1,
      // })
      const selectMinDate = DateTime.fromFormat(chartOptions.viewPeriodOptions.stDate, DATE_FORMAT.DEFAULT_FORMAT)
      const selectMaxDate = DateTime.fromFormat(chartOptions.viewPeriodOptions.endDate, DATE_FORMAT.DEFAULT_FORMAT)
      const seriesMinDate = DateTime.fromMillis(seriesMinMills)
      const seriesMaxDate = DateTime.fromMillis(seriesMaxMills)
      return {
        offsetMin: selectMinDate.diff(seriesMinDate, 'milliseconds').milliseconds,
        offsetMax: selectMaxDate.diff(seriesMaxDate, 'milliseconds').milliseconds,
      }
    }
    return {
      offsetMax: 0,
      offsetMin: 0
    }
  })

  const legendOption = computed(() => {
    const result: Options['legend'] = {
      ...legendOptions,
      labelFormatter: function () {
        return chartOptions.legendOptions.legendFormatter ?
          chartOptions.legendOptions.legendFormatter(this) :
          legendFormatter(this, chartOptions.legendOptions.showYAxisPosToLegend)
      },
    }
    if (!result) return {}

    if (chartOptions.chartDataList.length === 1) {
      result.enabled = false
    } else if (chartType.value === CHART_TYPE.PIE || chartType.value === CHART_TYPE.DONUT) {
      result.enabled = chartOptions.legendOptions.pieChartUseLegend
      result.align = 'right'
      result.verticalAlign = 'middle'
      result.layout = 'vertical'
      result.itemWidth = chartOptions.legendOptions.horizonLegendWidth ?? 160
    } else {
      result.enabled = true
      result.align = 'center'
      result.verticalAlign = 'bottom'
      result.layout = 'horizontal'
      result.alignColumns = false
    }
    return result
  })

  // eslint-disable-next-line ../../srctypescript-eslint/ban-ts-comment
  // @ts-ignore
  const highChartOptions = computed<Options>(() => {
    return {
      title: undefined,
      scrollbar: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      chart: {
        type: chartOptions.xAxisOptions.type === XAXIS_TYPE.CATEGORY ? 'column' : undefined,
        style: chartFontStyle,
        backgroundColor: 'transparent',
        zooming: {
          mouseWheel: {
            enabled: false,
          }
        },
        events: {
          load: function (this: ICustomChart) {
            if (watermark) {
              if (watermarkImg) watermarkImg.destroy()
              watermarkImg = addWatermark(this, chartType.value, watermark)
            }
            if (chartType.value === CHART_TYPE.PIE || chartType.value === CHART_TYPE.DONUT) {
              this.series[0].points.forEach((item, index) => {
                item?.legendItem?.on('mouseover', () => {
                  this.tooltip.refresh(this.series[0].data[index])
                })
              })
            }
          },
          redraw: function (this) {
            if (watermark) {
              if (watermarkImg) watermarkImg.destroy()
              watermarkImg = addWatermark(this, chartType.value, watermark)
            }
          },
        },
      },
      rangeSelector: {
        enabled: true,
        verticalAlign: 'none',
        allButtonsEnabled: true,
        buttons: chartOptions.viewPeriodOptions.viewPeriodList.map(ele => {
          if (ele.value.split('_').length > 1) {
            return {
              type: ele.value.split('_')[1] as RangeSelectorButtonTypeValue,
              count: Number(ele.value.split('_')[0]),
            }
          } else if (ele.value === 'selector') {
            return {
              type: 'all',
              text: 'selector',
              offsetMin: navigatorOffset.value.offsetMin,
              offsetMax: navigatorOffset.value.offsetMax,
            }
          } else {
            return {
              type: undefined,
            }
          }
        }),
        selected: selectedPeriodIdx.value,
      },
      navigator: {
        enabled: !(chartType.value === CHART_TYPE.PIE || chartType.value === CHART_TYPE.DONUT) && chartOptions.showNavigator,
        maskFill: 'rgba(var(--bc-dark), var(--o005))',
        outlineColor: 'rgba(var(--bc-gray), var(--o02))',
        outlineWidth: 1,
        handles: {
          backgroundColor: 'rgba(var(--bc-white), var(--o08))',
          borderColor: 'rgba(var(--bc-dark), var(--o02))',
          width: 14,
          height: 24,
        },
        xAxis: {
          type: 'datetime',
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: '\'Inter\', \'Noto Sans KR\', sans-serif',
            },
            formatter: function (this) {
              return xAxisFormatter(this, chartOptions.dateFormatLang, ref(0))
            },
          },
        },
      },
      xAxis: {
        // min: navigatorOffset.value.offsetMin,
        // max: navigatorOffset.value.offsetMax,
        visible: !(chartType.value === CHART_TYPE.PIE || chartType.value === CHART_TYPE.DONUT),
        type: chartOptions.xAxisOptions.type,
        title: {
          margin: 20,
          text: chartOptions.xAxisOptions.title,
          style: { color: 'rgba(var(--bc-dark), var(--o04))', fontSize:'12px', textOverflow: 'ellipsis' },
        },
        categories: chartOptions.xAxisOptions.type === XAXIS_TYPE.DATETIME ? undefined : xAxisCategories.value,
        minTickInterval: chartOptions.xAxisOptions.type === XAXIS_TYPE.DATETIME ? xAxisIntervalTime.value : undefined,
        labels: {
          style: chartFontStyle,
          formatter: chartOptions.xAxisOptions.type === XAXIS_TYPE.CATEGORY ? undefined :
            function (this) {
              return xAxisFormatter(this, chartOptions.dateFormatLang, xAxisIntervalTime)
            },
        },
        plotLines: [
          {
            color: 'rgba(var(--bc-dark), var(--o1))',
            dashStyle: 'ShortDash',
            width: 1,
            zIndex: 201,
          },
        ],
        events: {
          afterSetExtremes: function (evt) {
            if (chartOptions.yAxisOptions.useAbsMinMaxBySeries && chartOptions.chartDataList) {
              const minDate = evt.min
              const maxDate = evt.max
              const series = parsedChartSeries.value as IHighchartsSeries[]
              let maxValueInRange = 0
              series.map(ele => {
                const dateFilltered = ele.data.filter(
                  ele =>
                    // ele[0] >= minDate &&
                    //     ele[0] <= maxDate &&
                    //     ele[1] !== null &&
                    //     ele[1] !== undefined,
                    ele.x >= minDate && ele.x <= maxDate &&
                    ele.y !== null && ele.y !== undefined
                )
                const max = Math.max(
                  ...dateFilltered.map(ele => Math.abs(ele[1])),
                )
                maxValueInRange = maxValueInRange > max ? maxValueInRange : max
              })
              this.chart.yAxis[0].setExtremes(
                maxValueInRange * -1,
                maxValueInRange,
              )
              this.chart.yAxis[1].setExtremes(
                maxValueInRange * -1,
                maxValueInRange,
              )
            }
          },
        },
      },
      yAxis: getYAxisOptions(chartOptions),
      plotOptions: {
        area: {
          fillOpacity: 0.2,
          turboThreshold: 0,
        },
        line: {
          turboThreshold: 0,
        },
        arearange: {
          turboThreshold: 0,
        },
        // arearange: {
        //   color: {
        //     linearGradient: {
        //       x1: 0,
        //       x2: 0,
        //       y1: 0,
        //       y2: 1
        //     },
        //     stops: [
        //       [ 0, 'black' ],
        //       [ 1, '#6060FF' ]
        //     ]
        //   },
        // },
        column: {
          pointPadding: 0.1,
          groupPadding: 0.2,
        },
        pie: {
          cursor: 'pointer',
          innerSize: chartType.value === CHART_TYPE.DONUT ? '50%' : 0,
          size: '90%',
          showInLegend: true,
          opacity: chartType.value === CHART_TYPE.DONUT ? 1 : 0.8,
          dataLabels: {
            enabled: true,
            format: '{point.name}<br>{point.percentage:.2f}%',
            style: {
              fontWeight: '700',
              color: 'rgb(var(--bc-dark))',
              fontSize: '12px',
              textOutline: undefined,
              textOverflow: 'ellipsis',
            },
          },
        },
        sankey: {
          linkOpacity: 0.2,
          states: {
            hover: {
              linkOpacity: 0.8,
            },
          },
          colorByPoint: true,
          centerInCategory: true,

          borderWidth: 10,
          borderColor: 'transparent',

          minLinkWidth: 5,
          curveFactor: 0.4,
          nodeWidth: 16,
          nodePadding: 32,
          stickyTracking: true,
          relativeXValue: true,
          dataLabels: {
            align: 'right',
            verticalAlign: 'middle',
            x: -20,
            style: {
              textOutline: 'none',
              fontSize: '12px',
              color: 'var(--bc-dark)',
              whiteSpace: 'break-spaces',
            },
            nodeFormatter: function (this: SeriesSankeyDataLabelsFormatterContextObject & IPointExtendNode) {
              return sankeyNodeFormatter(this)
            }
          },
        },
        scatter: {
          marker: {
            enabled: true,
          },
        },
        series: {
          showInNavigator: true,
          borderWidth: 0,
          marker: {
            enabled: false,
          },
          dataGrouping: {
            enabled: false,
          },
          events: {
            legendItemClick: (e: SeriesLegendItemClickEventObject) => {
              chartOptions.chartDataList.forEach(ele => {
                if (ele.name === e.target.name) {
                  ele.visible = !ele.visible
                }
              })
            },
            // mouseOver: function () {
            //   chartOptions.chartDataList.forEach(series => {
            //     if (series.name !== this.userOptions.name) {
            //       series.opacity = 0.2
            //     }
            //   })
            // },
            // mouseOut: function () {
            //   chartOptions.chartDataList.forEach(series => {
            //     series.opacity = 1
            //   })
            // },
          },
        },
      },
      series: parsedChartSeries.value,
      colors: seriesColors.value,
      legend: legendOption.value,
      tooltip: {
        split: false,
        shared: true,
        backgroundColor: 'none',
        borderWidth: 0,
        borderRadius: 0,
        padding: 0,
        shadow: false,
        outside: true,
        useHTML: true,
        style: chartFontStyle,
        formatter: function (this) {
          if (chartOptions.tooltipOptions.customFormatter) {
            return chartOptions.tooltipOptions.customFormatter(this, chartOptions.tooltipOptions)
          } else {
            switch (chartType.value) {
              case CHART_TYPE.PIE:
              case CHART_TYPE.DONUT:
                return pieChartTooltipFormatter(this, chartOptions.tooltipOptions)
              case CHART_TYPE.SANKEY:
                return sankeyChartTooltipFormatter(this as ISankeyTooltipFormatterObject, chartOptions.tooltipOptions)
              default:
                return defaultTooltipFormatter(
                  this,
                  chartOptions.xAxisOptions,
                  chartOptions.viewPeriodOptions,
                  chartOptions.tooltipOptions,
                  chartOptions.dateFormatLang,
                )
            }
          }
        },
      },
    }
  })

  return {
    chartContainer,
    chartOptions,
    highChartOptions
  }
}